import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { IDsBreadcrumbItem } from '@levelaccess/design-system';
import { catchError, map, switchMap } from 'rxjs/operators';

import { TranslateService } from '../../../../translate/translate.service';
import { RoutingService } from '../../../../services/routing.service';
import { NotificationService } from '../../../../services/notification.service';
import { NotificationPosition } from '../../../../models/notification.model';
import { DigitalPropertyService } from '../../../../services/digital-property.service';
import { DigitalPropertyAction } from '../../../../../../shared/constants/digital-properties';
import { IDigitalPropertyClientForm } from '../../../../interfaces/digital-property.interface';
import { IDigitalProperty, IDigitalPropertyDetails } from '../../../../../../shared/interfaces/digital-property.interface';
import { Api } from '../../../../../../shared/constants/api';
import { ErrorHandlerService } from '../../../../services/error-handler.service';
import { componentScope } from '../../../../constants/component-scope';
import { UserService } from '../../../../services/user.service';
import { BusMessageChannels, BusMessageService } from '../../../../services/bus-message.service';
import { ITenant } from '../../../../../../shared/interfaces/tenant.interface';
import { IWorkspace } from '../../../../../../shared/interfaces/workspace.interface';
import { $tenant } from '../../../../../../shared/constants/tenant';
import { $workspace } from '../../../../../../shared/constants/workspace';
import { TenantService } from '../../../../services/tenant.service';
import { WorkspaceService } from '../../../../services/workspace.service';
import { TenantRouteUtil } from '../../../../admin/tenants/tenant-route.util';
import { LoadErrorHandler } from '../../../../services/load-error-handler';

@Component({
  selector: 'app-edit-tenant-workspace-digital-property',
  templateUrl: './edit-tenant-workspace-digital-property.component.html',
  standalone: false,
})
export class TenantWorkspaceDigitalPropertiesEditComponent implements OnInit, OnDestroy {
  private subscription: Subscription;
  private incomingFormData: IDigitalProperty;
  private digitalPropertyId: string;
  private readonly tenantRoutePath: string;

  public pageTitle: string;
  public formData: IDigitalPropertyClientForm;
  public digitalPropertyAction: typeof DigitalPropertyAction;
  public digitalProperty: IDigitalPropertyDetails;
  public breadcrumbs$: Observable<IDsBreadcrumbItem[]>;

  @Input() public scope: componentScope;
  @Input() public modal: boolean = false;
  @Input() public inputWorkspaceId?: string;
  @Input() public inputDigitalPropertyId?: string;
  @Input() public isWebsite?: boolean;

  @Output() public digitalPropertyEdited: EventEmitter<IDigitalPropertyDetails> = new EventEmitter();
  @Output() public cancelled: EventEmitter<void> = new EventEmitter();

  constructor(
    private errorHandlerService: ErrorHandlerService,
    private translateService: TranslateService,
    private routingService: RoutingService,
    private digitalPropertyService: DigitalPropertyService,
    private notificationService: NotificationService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private busMessageService: BusMessageService,
    private tenantService: TenantService,
    private workspaceService: WorkspaceService,
    private loadErrorHandler: LoadErrorHandler,
  ) {
    this.subscription = new Subscription();
    this.pageTitle = '';
    this.formData = null;
    this.digitalProperty = null;
    this.digitalPropertyAction = DigitalPropertyAction;
    this.tenantRoutePath = TenantRouteUtil.getTenantRoutePath(this.router.url);
  }

  private onUpdateDigitalPropertySuccess(editedDigitalProperty: IDigitalPropertyDetails): void {
    this.userService.reloadUser();
    this.busMessageService.to(BusMessageChannels.digitalPropertyChange).next();

    this.notificationService.success(
      this.translateService.instant('update_digital_property_success', this.incomingFormData.name),
      NotificationPosition.Toast,
      true,
    );

    if (this.modal) {
      this.digitalPropertyEdited.emit(editedDigitalProperty);
    } else {
      const tenantId: string = this.activatedRoute.snapshot.paramMap.get(Api.tenantId);
      const workspaceId: string = this.activatedRoute.snapshot.paramMap.get(Api.workspaceId);

      const redirectPath: string[] = [`${this.tenantRoutePath}/${tenantId}/${Api.workspaces}/${workspaceId}`];

      this.router.navigate(redirectPath).catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
    }
  }

  private onUpdateDigitalPropertyError(response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError(
      'error_editing_digital_asset_generic',
      response,
      TenantWorkspaceDigitalPropertiesEditComponent.name,
    );
  }

  private onGetDigitalPropertyError(response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('get_digital_property_error', response, TenantWorkspaceDigitalPropertiesEditComponent.name);
  }

  private onGetDigitalPropertySuccess(digitalProperty: IDigitalPropertyDetails): void {
    this.digitalProperty = digitalProperty;
  }

  public getDigitalPropertyData(): void {
    this.digitalPropertyId = this.inputDigitalPropertyId ?? this.activatedRoute.snapshot.paramMap.get(Api.digitalPropertyId);
    const workspaceId: string = this.inputWorkspaceId ?? this.activatedRoute.snapshot.paramMap.get(Api.workspaceId);

    this.subscription.add(
      this.digitalPropertyService.getDigitalProperty(workspaceId, this.digitalPropertyId).subscribe({
        next: this.onGetDigitalPropertySuccess.bind(this),
        error: this.onGetDigitalPropertyError.bind(this),
      }),
    );
  }

  public digitalPropertyAction$(data: IDigitalProperty): void {
    this.incomingFormData = data;

    this.subscription.add(
      this.digitalPropertyService.updateDigitalProperty(data, this.digitalPropertyId).subscribe({
        next: this.onUpdateDigitalPropertySuccess.bind(this),
        error: this.onUpdateDigitalPropertyError.bind(this),
      }),
    );
  }

  public onCancel(): void {
    if (this.modal) {
      this.cancelled.emit();
    }
  }

  public ngOnInit(): void {
    this.pageTitle = this.routingService.getPageTitle();
    this.getDigitalPropertyData();

    const tenant$: Observable<ITenant> = this.activatedRoute.paramMap.pipe(
      map((params: ParamMap) => params.get(Api.tenantId)),
      switchMap((tenantId: string) => this.tenantService.getTenant(tenantId)),
      catchError(this.errorHandlerService.handleNotFound()),
    );
    const workspace$: Observable<IWorkspace> = this.activatedRoute.paramMap.pipe(
      map((params: ParamMap) => params.get(Api.workspaceId)),
      switchMap((workspaceId: string) => this.workspaceService.getWorkspace(workspaceId)),
      catchError(this.errorHandlerService.handleNotFound()),
    );

    this.breadcrumbs$ = combineLatest([tenant$, workspace$]).pipe(
      map(([tenant, workspace]: [ITenant, IWorkspace]) => {
        return [
          {
            label: this.translateService.instant('tenants'),
            rtLink: `${this.tenantRoutePath}`,
          },
          {
            label: tenant[$tenant.name],
            rtLink: `${this.tenantRoutePath}/${tenant[$tenant._id]}`,
          },
          {
            label: workspace[$workspace.name],
            rtLink: `${this.tenantRoutePath}/${tenant[$tenant._id]}/${Api.workspaces}/${workspace[$workspace._id]}`,
          },
          {
            label: this.translateService.instant(this.pageTitle),
            rtLink: '.',
          },
        ];
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
